import AnimateInView from '@atoms/AnimateInView/AnimateInView';
import SiteEnum from '@models/enums/SiteEnum';
import { IArticleCard } from '@models/IArticleCard';
import { IArticleFilterItem, IArticleListing } from '@models/IArticleListing';
import { PageTitleSize } from '@models/IPageTitle';
import ArticleCard from '@molecules/ArticleCard/ArticleCard';
import ArticleListingLinkCard from '@molecules/ArticleListingLinkCard/ArticleListingLinkCard';
import FilterList from '@molecules/FilterList/FilterList';
import InfiniteScroll from '@molecules/InfiniteScroll/InfiniteScroll';
import PageTitle from '@molecules/PageTitle/PageTitle';
import HeadlineAndTextBlock from '@organisms/HeadlineAndTextBlock/HeadlineAndTextBlock';
import { fadeInUp, fadeTransition } from '@utility/config/animations';
import useListingComponentData from '@utility/hooks/useListingComponentData';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';

type FetchPayload = {
  filterIds: string;
  epslanguage?: string;
  take: number;
  skip: number;
  pageLinkId?: string;
};

type QueryParams = {
  filterIds?: string;
};

const cardProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: fadeTransition,
  className: 'tw-col-span-4 md:tw-col-span-3 lg:tw-col-span-6 xl:tw-col-span-4',
};

const ArticleListing = ({
  filters,
  infiniteLoadLabel,
  initialItems,
  language,
  listingCard,
  loadingLabel,
  pageLinkId,
  pageSize,
  searchEndpoint,
  showListingCardWhenFiltered,
  site,
  smallTitle = false,
  text,
  title,
  totalResults,
}: IArticleListing) => {
  const [listItems, setListItems] = useState(initialItems ?? []);
  const [loadingMessage, setLoadingMessage] = useState(loadingLabel);
  const isMobile = useMediaQuery(767);
  const listingCardPosition = isMobile ? 4 : 6;

  //'Take' value varies depending on the presence of the Listing Card and page number
  const getTakeValue = (noActiveFilter: boolean, skip: number) => {
    const hasListingCard =
      (noActiveFilter || showListingCardWhenFiltered) && listingCard;
    const isPageOne = skip === 0;

    return hasListingCard && isPageOne ? pageSize - 1 : pageSize;
  };

  const [queryState, setQueryState] = useState<
    | {
        activeFilter: IArticleFilterItem | null;
        skip: number;
        take: number;
        isDefaultData: boolean;
        pageLinkId: string;
      }
    | undefined
  >();

  // Populate queryState using url query params on client side
  useEffect(() => {
    const params: QueryParams =
      typeof window !== 'undefined'
        ? queryString.parse(
            queryString.pick(window?.location.search, ['filterIds'])
          )
        : { filterIds: undefined };

    const activeFilter = params.filterIds
      ? getFilterByID(params.filterIds, filters)
      : null;

    setQueryState({
      skip: 0,
      take: getTakeValue(!activeFilter, 0),
      activeFilter,
      isDefaultData: !activeFilter && !!initialItems,
      pageLinkId: pageLinkId,
    });
  }, [queryString]);

  const { fetchData, state } = useListingComponentData<
    FetchPayload,
    { items: IArticleCard[]; totalResults: number }
  >(
    searchEndpoint,
    { items: initialItems ?? [], totalResults: totalResults ?? 0 },
    false
  );

  useEffect(() => {
    // Flag to ensure fetch doesn't run on page load
    // default data is provided by items prop
    if (!queryState || queryState.isDefaultData) return;

    let payload: FetchPayload = {
      filterIds: queryState.activeFilter?.id ?? '',
      take: queryState.take,
      skip: queryState.skip,
      pageLinkId: queryState.pageLinkId,
    };

    if (language && pageLinkId) {
      payload = {
        ...payload,
        epslanguage: language,
        pageLinkId,
      };
    }

    if (queryState.skip === 0) setListItems([]);
    fetchData(payload);
  }, [queryState]);

  useEffect(() => {
    // If skip is greater than 0 then the next page of data should be appended
    if (queryState && queryState.skip > 0) {
      setListItems([...listItems, ...state.data.items]);
      return;
    }

    setListItems(state.data.items);
  }, [state.data]);

  return (
    <section>
      <div
        className={classNames(
          'tw-flex tw-flex-col tw-justify-center tw-gap-8 tw-pt-12 md:tw-gap-10',
          !smallTitle && 'tw-pb-8 md:tw-pb-12'
        )}
      >
        <PageTitle
          isMainHeading={!smallTitle}
          variant={smallTitle ? PageTitleSize.Small : PageTitleSize.Large}
          title={title}
          content={text}
          site={SiteEnum.B2B}
          hasPadding={false}
        />
        {filters ? (
          <AnimateInView>
            <motion.div
              variants={fadeInUp}
              className="tw-mx-auto tw-max-w-[343px] md:tw-max-w-[671px]"
            >
              <FilterList
                filters={filters}
                onSelect={(id: string): void => {
                  setLoadingMessage(loadingLabel);
                  const activeFilter =
                    id === queryState?.activeFilter?.id
                      ? null
                      : getFilterByID(id, filters);

                  setQueryState({
                    skip: 0,
                    take: getTakeValue(!activeFilter, 0),
                    isDefaultData: false,
                    activeFilter,
                    pageLinkId,
                  });
                }}
                selectedItemID={queryState?.activeFilter?.id ?? null}
                site={site}
              />
            </motion.div>
          </AnimateInView>
        ) : null}
      </div>
      {queryState?.activeFilter?.header ? (
        <div className="tw-bg-grey-10 tw-py-12 md:tw-py-16">
          <HeadlineAndTextBlock
            {...queryState.activeFilter?.header}
            site={SiteEnum.B2B}
          />
        </div>
      ) : null}
      <InfiniteScroll
        loadingLabel={loadingMessage}
        containerProps={{
          className:
            'tw-grid-container tw-min-h-[500px] tw-gap-x-8 tw-gap-y-8 tw-py-8 md:tw-py-12 tw-mb-[90px] lg:tw-mb-[120px] lg:tw-gap-y-16',
        }}
        isLoading={state.loading}
        isLastPage={listItems?.length >= state.data.totalResults}
        onLoadMore={() => {
          setLoadingMessage(infiniteLoadLabel);

          const skip = listItems.length;
          if (skip < state.data.totalResults && queryState) {
            const take = getTakeValue(!queryState.activeFilter, skip);
            setQueryState({ ...queryState, skip, take, isDefaultData: false });
          }
        }}
      >
        <AnimatePresence>
          {queryState &&
            (!state.loading || queryState.skip > 0) &&
            listItems.length > 0 && (
              <>
                {listItems?.map((card: IArticleCard) => (
                  <motion.div {...cardProps} key={card.id ?? card.title}>
                    <ArticleCard
                      {...card}
                      truncateTitle={false}
                      titleVariant="ds-headline-3"
                    />
                  </motion.div>
                ))}
                {
                  // Show listing card only when no filters are active unless the showListingCardWhenFiltered prop is active
                  (!queryState.activeFilter || showListingCardWhenFiltered) &&
                  listingCard ? (
                    <motion.div
                      {...cardProps}
                      className={classNames(
                        cardProps.className,
                        listItems.length >= listingCardPosition &&
                          'tw-row-start-4 md:tw-col-start-4 md:tw-row-start-3 lg:tw-col-start-7 xl:tw-col-start-9 xl:tw-row-start-2'
                      )}
                      key={listingCard.title}
                    >
                      <ArticleListingLinkCard {...listingCard} />
                    </motion.div>
                  ) : null
                }
              </>
            )}
        </AnimatePresence>
      </InfiniteScroll>
    </section>
  );
};

const getFilterByID = (
  id: string,
  filters: IArticleFilterItem[]
): IArticleFilterItem | null => {
  return filters.find((filter) => filter.id === id) ?? null;
};

export default ArticleListing;
